import React, { useEffect, useState } from 'react';
import styled from 'astroturf/react';

import { isDownloadRead } from '../openerIntent';
import type { ArmSpec } from '../armRegistry';
import { getWorkContext } from '../workContext';

const REST_PCT = 90;
const START_PCT = 12;
const COMPLETE_MS = 400;
const WRAP_TIMEOUT_MS = 1500;

// The one seam between the mounted strip and wrapOnSuccess: the strip
// publishes its completion driver here on mount and clears it on unmount.
let completeActiveBar: (() => Promise<void>) | null = null;

// The modal tree stays mounted across close/reopen (dialog CSS toggle) and
// React reconciles a same-type slot node in place, so without a fresh key a
// reopened modal would show the previous open's frozen 90% bar.
let openSequence = 0;

function prefersReducedMotion(): boolean {
  return (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xxs-8, 8px);
  margin-bottom: var(--spacing-xs-16, 16px);
`;

const Label = styled.span`
  font-family: var(--type-font-family-sans-serif, 'DM Sans', sans-serif);
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary, #141413);
  text-align: center;
`;

const Track = styled.div`
  width: 100%;
  height: 6px;
  border-radius: var(--border-radius-radius-full, 999px);
  background: var(--stroke-primary, #dddde2);
  overflow: hidden;
`;

const Fill = styled.div`
  height: 100%;
  border-radius: inherit;
  background: var(--primary-brand, #0645b1);
  transition: width 0.6s ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const DownloadPreflightStrip: React.FC<{ pageCount: number }> = ({
  pageCount,
}) => {
  const reducedMotion = prefersReducedMotion();
  const [progress, setProgress] = useState(
    reducedMotion ? REST_PCT : START_PCT,
  );

  useEffect(() => {
    const complete = (): Promise<void> => {
      setProgress(100);
      if (reducedMotion) return Promise.resolve();
      return new Promise((resolve) => {
        window.setTimeout(resolve, COMPLETE_MS);
      });
    };
    completeActiveBar = complete;

    let raf = 0;
    if (!reducedMotion) {
      raf = window.requestAnimationFrame(() => setProgress(REST_PCT));
    }
    return () => {
      if (completeActiveBar === complete) completeActiveBar = null;
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [reducedMotion]);

  const label = `Preparing your PDF · ${pageCount} ${
    pageCount === 1 ? 'page' : 'pages'
  }`;

  return (
    <Wrapper>
      <Label>{label}</Label>
      <Track
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={progress}
      >
        <Fill style={{ width: `${progress}%` }} />
      </Track>
    </Wrapper>
  );
};

// Fail-safe: navigation must never be lost, so the bar-completion race lives
// in try and navigate(redirect) runs in finally regardless of the outcome.
export async function settleThenNavigate(
  work: Promise<void>,
  navigate: (redirect?: string) => void,
  redirect: string | undefined,
): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const timeout = new Promise<void>((resolve) => {
      timer = setTimeout(resolve, WRAP_TIMEOUT_MS);
    });
    await Promise.race([work, timeout]);
  } finally {
    if (timer) clearTimeout(timer);
    navigate(redirect);
  }
}

export const downloadPreflightSpec: ArmSpec = {
  stamp: (ctx) => {
    if (!isDownloadRead(ctx.detail)) return undefined;
    const pageCount = getWorkContext()?.pageCount;
    const renders = pageCount != null && pageCount > 0;
    return { render_state: renders ? 'rendered' : 'fallback' };
  },
  slots: (ctx) => {
    if (!isDownloadRead(ctx.detail)) return undefined;
    const pageCount = getWorkContext()?.pageCount;
    if (pageCount == null || pageCount <= 0) return undefined;
    openSequence += 1;
    return {
      aboveHeader: (
        <DownloadPreflightStrip key={openSequence} pageCount={pageCount} />
      ),
    };
  },
  wrapOnSuccess: (navigate) => (redirect) => {
    const work = completeActiveBar ? completeActiveBar() : Promise.resolve();
    void settleThenNavigate(work, navigate, redirect).catch(() => undefined);
  },
};
